import { EMPTY_OBJECT } from '../../constants/common'
import {
  ROUTE_PATH_LIST,
  ROUTE_UI_SECTION_NAME,
  SUBMENU_ACCOUNT_PARAM_KEY,
} from '../../constants/route'
import { LANDLORD_USER_ROLE } from '../../constants/user'
import { getSectionCanViewPermission } from '../../utils/common'
import { getWebappLinkPrefix } from '../../utils/route'

import {
  BIRDVIEW_DEFAULT_ROLE,
  BIRDVIEW_REQUEST_KEY_SEPARATOR,
  BIRDVIEW_ROLES,
  BirdviewScope,
  BirdviewSection,
  BirdviewSkeleton,
  BirdviewState,
  BirdviewView,
  EMPTY_SECTION,
} from './DashboardBirdview.constants'
import { BIRDVIEW_SECTION_ITEMS_LIMIT } from './DashboardBirdview.constants.api'
import {
  BIRDVIEW_ATTENTION_AGE_OPTIONS,
  BIRDVIEW_ATTENTION_DEFAULT_AGE,
} from './DashboardBirdview.constants.attention'
import { BirdviewAnalyticsPreset } from './DashboardBirdview.constants.charts'

export function getBirdviewGreetingKey(date) {
  const hour = date.getHours()

  if (hour < 12) {
    return 'DashboardBirdview.GreetingMorningKey'
  }

  if (hour < 18) {
    return 'DashboardBirdview.GreetingAfternoonKey'
  }

  return 'DashboardBirdview.GreetingEveningKey'
}

export function getBirdviewRole(userRole) {
  return (
    BIRDVIEW_ROLES.find(role => role.value === userRole) ??
    BIRDVIEW_ROLES.find(role => role.value === BIRDVIEW_DEFAULT_ROLE)
  )
}

export function checkIsBirdviewOversightSkeleton(role) {
  return role.skeleton === BirdviewSkeleton.Oversight
}

export function checkIsBirdviewDwellerSkeleton(role) {
  return role.skeleton === BirdviewSkeleton.Dweller
}

export function checkIsBirdviewLandlordRole(role) {
  return role.value === LANDLORD_USER_ROLE
}

export function checkIsBirdviewTeamScope(scope) {
  return scope === BirdviewScope.Team
}

export function getBirdviewAnalyticsPreset(role) {
  return checkIsBirdviewOversightSkeleton(role)
    ? BirdviewAnalyticsPreset.Portfolio
    : BirdviewAnalyticsPreset.Personal
}

export function checkIsBirdviewAnalyticsView(view) {
  return view === BirdviewView.Analytics
}

// A team pick made as an admin must not leak into a role that has no switch to
// undo it.
export function getBirdviewEffectiveScope({ role, scope }) {
  return checkIsBirdviewOversightSkeleton(role) ? scope : BirdviewScope.Mine
}

export function getBirdviewEffectiveView({ role, view }) {
  return checkIsBirdviewDwellerSkeleton(role) ? BirdviewView.Dashboard : view
}

export function getBirdviewSectionState({ hasError, isLoading, items }) {
  if (hasError) {
    return BirdviewState.Error
  }

  if (isLoading) {
    return BirdviewState.Loading
  }

  if (items.length === 0) {
    return BirdviewState.Empty
  }

  return BirdviewState.Normal
}

export function getBirdviewLoadState({ hasError, isLoading }) {
  if (hasError) {
    return BirdviewState.Error
  }

  return isLoading ? BirdviewState.Loading : BirdviewState.Normal
}

export function checkIsBirdviewNormalState(state) {
  return state === BirdviewState.Normal
}

export function checkIsBirdviewLoadingState(state) {
  return state === BirdviewState.Loading
}

export function checkIsBirdviewErrorState(state) {
  return state === BirdviewState.Error
}

export function checkIsBirdviewEmptyState(state) {
  return state === BirdviewState.Empty
}

export function checkIsBirdviewDocFlowsViewable(sectionsPermissions) {
  return getSectionCanViewPermission(
    sectionsPermissions,
    ROUTE_UI_SECTION_NAME.docFlows
  )
}

export function checkIsBirdviewChatsViewable(sectionsPermissions) {
  return getSectionCanViewPermission(
    sectionsPermissions,
    ROUTE_UI_SECTION_NAME.chats
  )
}

export function checkIsBirdviewDwellerDocumentsViewable(sectionsPermissions) {
  return getSectionCanViewPermission(
    sectionsPermissions,
    ROUTE_UI_SECTION_NAME.documentsDweller
  )
}

// A dashboard-wide reload carries no section key.
export function checkIsBirdviewActivitiesReload(sectionKey) {
  return !sectionKey || sectionKey === BirdviewSection.Activities
}

export function checkIsBirdviewDashboardReload(sectionKey) {
  return !sectionKey
}

export function checkIsBirdviewSectionsLoading(sections) {
  return Object.values(sections).some(section => section.isLoading)
}

// A list the NG app does not serve yet opens in the old webapp, the way the
// primary menu resolves its items.
export function getBirdviewListTarget({ availableSections, target }) {
  return availableSections[target.sectionName]
    ? target.path
    : getWebappLinkPrefix(target.path)
}

export function checkIsBirdviewActivityDone(item, activityDoneById) {
  return activityDoneById[item.id] ?? item.done
}

// `null` is a pending "remove all", so the presence of the key wins over `??`.
export function getBirdviewRequestAssignee(item, assigneeById) {
  return Object.hasOwn(assigneeById, item.id)
    ? assigneeById[item.id]
    : item.assignee
}

// The row hands its section reload to the action so a failed mutation can
// bring the server state back (BR-DASH-29).
export function getBirdviewRowRequestActions({ requestActions, onReload }) {
  return requestActions
    ? {
        ...requestActions,
        onAssign: item => requestActions.onAssign(item, onReload),
      }
    : EMPTY_OBJECT
}

export function getBirdviewRequestKey(params, reloadToken) {
  return [...Object.values(params), reloadToken].join(
    BIRDVIEW_REQUEST_KEY_SEPARATOR
  )
}

// A section the dashboard never requested has no state at all.
export function getBirdviewSection({
  data,
  hasError,
  isLoading,
  reload,
} = EMPTY_OBJECT) {
  const section = data ?? EMPTY_SECTION
  const items = section.items.slice(0, BIRDVIEW_SECTION_ITEMS_LIMIT)

  return {
    extra: section.extra,
    hasMore: section.total > items.length,
    items,
    reload,
    state: getBirdviewSectionState({ hasError, isLoading, items }),
    total: section.total,
  }
}

export function getBirdviewTiles({ definitions, sections }) {
  return definitions.map(tile => {
    const section = sections[tile.sectionKey]

    return { ...tile, ...section.data, state: getBirdviewLoadState(section) }
  })
}

export function getBirdviewAttentionAgeOption(value) {
  return (
    BIRDVIEW_ATTENTION_AGE_OPTIONS.find(option => option.value === value) ??
    BIRDVIEW_ATTENTION_DEFAULT_AGE
  )
}

export function checkIsBirdviewAttentionDefaultAge(age) {
  return age.value === BIRDVIEW_ATTENTION_DEFAULT_AGE.value
}

export function getBirdviewAttentionEmptyTextKey(age) {
  return checkIsBirdviewAttentionDefaultAge(age)
    ? 'DashboardBirdview.EmptyStateKey'
    : 'DashboardBirdview.AttentionAgeEmptyStateKey'
}

export function getBirdviewMailboxTarget(mailboxId) {
  return {
    path: `/${ROUTE_PATH_LIST.mails}?${SUBMENU_ACCOUNT_PARAM_KEY}=${mailboxId}`,
    sectionName: ROUTE_UI_SECTION_NAME.mails,
  }
}

export function getBirdviewFlatNumberLabel(number, t) {
  if (number) {
    return `${t('DashboardBirdview.FlatNumberKey')} ${number}`
  }
}

export function sortBirdviewLoadItems(items) {
  return [...items].sort(
    (first, second) =>
      second.overdue - first.overdue || second.open - first.open
  )
}
